import api from "@/lib/axiosConfig";
import { Bibliography, FetchedBibliography } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { RelativePosition } from "../dtos";

export const baseRoute = "back/api/bibliography";

interface BibliographyDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

export const mapFetchedBibliography = (
  bibliography: FetchedBibliography,
): Bibliography => ({
  ...bibliography,
  //TECH_DEBT
  bibliographieDescriptionDimensions: parseJsonOrUndefined(
    bibliography.bibliographieDescriptionDimensions,
  ),
});

async function getBibliographies({
  where,
  order,
  take,
  skip,
}: BibliographyDTO): Promise<Bibliography[]> {
  const { data } = await api.post(
    `back/api/bibliography?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data.map(mapFetchedBibliography);
}

export const useGetAllBibliographies = ({
  where,
  order,
  take,
  skip,
}: BibliographyDTO) => {
  return useQuery({
    queryKey: ["bibliographies", where, order, take, skip],
    queryFn: () => getBibliographies({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

const getBibliography = async (id: string): Promise<Bibliography> => {
  const { data } = await api.get<FetchedBibliography>(
    `/back/api/bibliography/${id}`,
  );
  return mapFetchedBibliography(data);
};

export const useGetBibliography = (id?: string) => {
  return useQuery({
    queryKey: ["bibliography", id],
    queryFn: () => (id ? getBibliography(id) : undefined),
  });
};

export async function countByStatus(where?: object) {
  const { data } = await api.post(`back/api/bibliography/countByStatus`, {
    where,
  });
  return data;
}

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

export const useCountByStatus = (where?: object) => {
  return useQuery({
    queryKey: ["countByStatus", "bibliography", where],
    queryFn: () => countByStatus(where),
  });
};

async function updateBibliography(bibliography: Bibliography) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = bibliography;
  return await api.patch(`back/api/bibliography/${bibliography.id}`, {
    data: {
      ...rest,
      //TECH_DEBT
      bibliographieDescriptionDimensions: JSON.stringify(
        bibliography.bibliographieDescriptionDimensions,
      ),
    },
  });
}

export function useUpdateBibliography() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBibliography,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["bibliography", v.id] });
      queryClient.invalidateQueries({ queryKey: ["bibliographies"] });
    },
  });
}

type CreateBibliographyParams = {
  bibliographieDescriptionNumeroBibliographie: string;
  bibliographieDescriptionTitre: string;
};
type CreateBibliographyResponse = Pick<Bibliography, "id">;

const createBibliography = async ({
  bibliographieDescriptionNumeroBibliographie,
  bibliographieDescriptionTitre,
}: CreateBibliographyParams) => {
  return await api
    .post<CreateBibliographyResponse>("back/api/bibliography/create", {
      bibliographieDescriptionNumeroBibliographie,
      bibliographieDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreateBibliography = (
  options?: UseMutationOptions<
    CreateBibliographyResponse,
    Error,
    CreateBibliographyParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createBibliography,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["bibliographies"] });
      queryClient.invalidateQueries({ queryKey: ["bibliography", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "bibliography"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const getAllIds = async (where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids`, {
    where,
  });
  return data;
};
